import { LogOut } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  Button,
  Card,
  PageSection,
  StatusMessage,
} from "@ceedero/ui";

import { useSignOutEverywhere } from "@/account/hooks/use-account";
import { resolveCredentialChangeErrorKey } from "@/account/utils/account-refusal";
import { useTranslation } from "@/localization/hooks/use-translation";

/**
 * Signing out every browser signed in to this account.
 *
 * Asked about rather than done outright: it acts on machines the user cannot
 * see from here, and the confirmation is the only place it can be said what is
 * and is not affected — every browser including this one, and no desktop
 * installation, which holds its own credential and is reached only by changing
 * the password.
 *
 * There is no list of sessions to choose from, deliberately. The API exposes
 * none, and inventing one from what a browser can observe about itself would be
 * a fingerprint that is wrong behind a shared connection.
 */
export const SessionSection = () => {
  const { t } = useTranslation();
  const signOutEverywhere = useSignOutEverywhere();
  const [isConfirming, setIsConfirming] = useState(false);

  const refusalKey = resolveCredentialChangeErrorKey(signOutEverywhere.error);

  const confirm = () => {
    setIsConfirming(false);
    signOutEverywhere.mutate();
  };

  return (
    <PageSection
      title={t("account.sessions.title")}
      description={t("account.sessions.description")}
    >
      <Card className="flex flex-col gap-4 p-5">
        {refusalKey !== undefined && <StatusMessage tone="error">{t(refusalKey)}</StatusMessage>}

        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsConfirming(true)}
            isLoading={signOutEverywhere.isPending}
          >
            <LogOut />
            {t("account.sessions.signOutEverywhere")}
          </Button>
        </div>
      </Card>

      <AlertDialog
        open={isConfirming}
        onOpenChange={(open) => {
          if (!open) {
            setIsConfirming(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogTitle>{t("account.sessions.confirmTitle")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("account.sessions.confirmDescription")}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="ghost">
                {t("account.cancel")}
              </Button>
            </AlertDialogCancel>
            <Button type="button" variant="destructive" onClick={confirm}>
              {t("account.sessions.signOutEverywhere")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageSection>
  );
};
